import { html, css, TemplateResult, CSSResultGroup, svg } from 'lit'
import { customElement, property, queryAssignedElements, state } from 'lit/decorators.js'
import { BaseComponent } from './base-component.js'
import { animate } from '@lit-labs/motion'
import { animateHeightContainer } from './motion/animations.js'

@customElement('il-card')
export default class CardComponent extends BaseComponent {
  @property({ type: Boolean })
  expanded: boolean = false

  @property({ type: String })
  type: 'default' | 'theme' | 'warning' = 'default'

  @queryAssignedElements({ slot: 'trigger' })
  triggerSlotElements?: Element[]

  @state()
  hasTriggerSlot: boolean = false

  _onTriggerSlotChange(): void {
    this.hasTriggerSlot = Boolean(this.triggerSlotElements?.length)
  }

  _emitToggled(): void {
    if (!this.hasTriggerSlot) return

    const event = new Event('toggled', { bubbles: true, composed: true })
    this.dispatchEvent(event)
  }

  downCarat = svg`
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3 7.28114L10 14.3L17 7.28113L15.7055 6L10 11.7209L4.29449 6L3 7.28114Z" fill="currentColor"/>
    </svg>
    `

  upCarat = svg`
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3 13.0189L10 6L17 13.0189L15.7055 14.3L10 8.57911L4.29449 14.3L3 13.0189Z" fill="currentColor"/>
    </svg>
    `

  override render(): TemplateResult {
    return html`
      <div
        data-testid="il-card"
        class="${this.baseCSSClass} il-card ${this.hasTriggerSlot ? 'il-card--expandable' : ''} il-card--${this.type}"
      >
        <div
          data-testid="il-card__content"
          class="il-card__content ${!this.expanded && this.hasTriggerSlot ? 'il-card__content--collapsed' : ''}"
          ${animate(
            animateHeightContainer({
              fast: this.fastAnimation,
              animationCurve: this.animationCurve,
            })
          )}
        >
          <slot></slot>
          <slot name="section"></slot>
          <slot name="image"></slot>
        </div>

        <div
          data-testid="il-card__trigger-container"
          class="il-card__trigger-container ${this.hasTriggerSlot ? '' : 'il-card__trigger-container--hidden'}"
          .role=${this.hasTriggerSlot ? 'button' : ''}
          @click=${this._emitToggled}
        >
          ${this.expanded ? this.downCarat : this.upCarat}
          <slot name="trigger" @slotchange=${this._onTriggerSlotChange}></slot>
        </div>
      </div>
    `
  }

  static override styles: CSSResultGroup = [
    BaseComponent.styles,
    css`
      .il-card {
        display: flex;
        flex-direction: column;

        border-radius: 6px;
        border: 1px solid var(--il-color-neutrals-100);
        background-color: var(--il-color-neutrals-50);
      }

      .il-card.il-card--theme {
        border-radius: 12px;
        border: 2px solid var(--il-color-accent-100);
        background-color: var(--il-color-accent-50);
      }

      .il-card.il-card--warning {
        border-radius: 12px;
        border: 2px solid var(--il-color-warning);
        background-color: var(--il-color-neutrals-50);
        color: var(--il-color-warning);
      }

      .il-card__content {
        display: flex;
        flex-direction: column;
        overflow: hidden;
      }
      .il-card__content--collapsed {
        height: 0;
      }

      .il-card ::slotted([slot]:not(:last-child)) {
        border-bottom: 1px solid var(--il-color-neutrals-100) !important;
      }

      .il-card ::slotted([slot='section']),
      .il-card .il-card__trigger-container {
        padding: 12px 16px !important;
      }

      .il-card ::slotted([slot='image']) {
        padding: 8px 16px 8 8px !important;
      }

      .il-card .il-card__trigger-container {
        width: 100%;

        display: flex;
        align-items: center;
        gap: 8px;

        cursor: pointer;
      }

      .il-card.il-card--expandable .il-card__trigger-container {
        padding: 12px 16px 12px 12px !important;
      }

      .il-card__trigger-container--hidden {
        display: none !important;
      }
    `,
  ]
}

declare global {
  interface HTMLElementTagNameMap {
    'il-card': CardComponent
  }
}
